import React, { FC, useState } from 'react';
import { useLoginPrompt } from '../common/LoginPromptContext';
import { useDealForProperty } from '../../hooks/api/Wholesale/useWholesale';

// The deal's photos one at a time, in the order they were uploaded, with Previous / Next buttons.
export const PhotoViewer: FC<{ urls: string[] }> = ({ urls }) => {
  const [ index, setIndex ] = useState(0);
  if (urls.length === 0) return null;

  const current = Math.min(index, urls.length - 1);
  const go = (next: number) => setIndex((next + urls.length) % urls.length);

  return (
    <div
      data-testid="wholesale-photo-viewer"
      className="mt-3"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') go(current + 1);
        if (e.key === 'ArrowLeft') go(current - 1);
      }}
    >
      <div className="relative flex items-center justify-center rounded bg-black/5">
        <img src={urls[current]} alt={`Wholesale deal photo ${current + 1} of ${urls.length}`} className="max-h-96 w-full rounded object-contain" />
        {urls.length > 1 && (
          <>
            <button type="button" aria-label="Previous photo" onClick={() => go(current - 1)} className="absolute left-2 rounded-full bg-white/90 px-3 py-2 font-bold shadow hover:bg-white">‹</button>
            <button type="button" aria-label="Next photo" onClick={() => go(current + 1)} className="absolute right-2 rounded-full bg-white/90 px-3 py-2 font-bold shadow hover:bg-white">›</button>
          </>
        )}
      </div>
      <p className="mt-1 text-center text-xs text-gray-700" data-testid="wholesale-photo-count">Photo {current + 1} of {urls.length}</p>
      {urls.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {urls.map((url, i) => (
            <button key={url} type="button" aria-label={`Show photo ${i + 1}`} onClick={() => setIndex(i)} className={`rounded ${i === current ? 'ring-2 ring-amber-500' : 'opacity-70 hover:opacity-100'}`}>
              <img src={url} alt="" className="h-14 w-20 rounded object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Shown to Pro members when this property has a live wholesale deal. Renders nothing otherwise
// (no deal, expired, or not a Pro member), so it is safe to place on every property page.
export const WholesaleDealPanel: FC<{ propertyId?: string }> = ({ propertyId }) => {
  const { isLoggedIn } = useLoginPrompt();
  const { deal } = useDealForProperty(propertyId, isLoggedIn);
  if (!deal) return null;

  return (
    <section data-testid="wholesale-deal-panel" className="mx-6 mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-gray-900">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-bold">Wholesale deal: ${Number(deal.wholesale_price).toLocaleString()}</h2>
        <p className="text-gray-700">{deal.deal_type_label} · expires {deal.expires_on}</p>
      </div>
      {deal.notes && <p className="mt-2 whitespace-pre-line">{deal.notes}</p>}
      <PhotoViewer urls={deal.photo_urls} />
      {deal.video_urls.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {deal.video_urls.map((url) => <video key={url} src={url} controls className="h-20 rounded" />)}
        </div>
      )}
      {deal.contact && (
        <p className="mt-3 text-gray-800" data-testid="wholesale-contact">
          Contact {deal.contact.name}{deal.contact.company ? ` (${deal.contact.relationship ?? 'rep'} of ${deal.contact.company})` : ''}
          {deal.contact.phone ? ` · ${deal.contact.phone}` : ''}{deal.contact.email ? ` · ${deal.contact.email}` : ''}
        </p>
      )}
    </section>
  );
};
